import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { approvalsAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const TABS = ['pending', 'my-requests', 'rules'];
const PRIORITY_COLORS = { urgent: 'badge-danger', high: 'badge-warning', normal: 'badge-info', low: 'badge-primary' };
const STATUS_COLORS = { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger', cancelled: 'badge-info', expired: 'badge-info' };

const Approvals = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const storeId = selectedStore?.id;
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pending
  const [pending, setPending] = useState([]);
  const [pendingFilter, setPendingFilter] = useState({ type: '', priority: '' });

  // My Requests
  const [myRequests, setMyRequests] = useState([]);
  const [myFilter, setMyFilter] = useState({ status: '' });

  // Rules
  const [rules, setRules] = useState([]);
  const [ruleForm, setRuleForm] = useState(null);

  // Modal
  const [respondModal, setRespondModal] = useState(null);
  const [respondNotes, setRespondNotes] = useState('');

  const loadPending = useCallback(async () => {
    if (!storeId) return;
    try {
      const params = {};
      if (pendingFilter.type) params.approval_type = pendingFilter.type;
      if (pendingFilter.priority) params.priority = pendingFilter.priority;
      const res = await approvalsAPI.listPending(storeId, params);
      setPending(Array.isArray(res.data) ? res.data : res.data.requests || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [storeId, pendingFilter]);

  const loadMyRequests = useCallback(async () => {
    if (!storeId) return;
    try {
      const params = {};
      if (myFilter.status) params.status = myFilter.status;
      const res = await approvalsAPI.getMyRequests(storeId, params);
      setMyRequests(Array.isArray(res.data) ? res.data : res.data.requests || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [storeId, myFilter]);

  const loadRules = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await approvalsAPI.listRules(storeId, false);
      setRules(Array.isArray(res.data) ? res.data : res.data.rules || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [storeId]);

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      if (activeTab === 'pending') await loadPending();
      else if (activeTab === 'my-requests') await loadMyRequests();
      else if (activeTab === 'rules') await loadRules();
      setLoading(false);
    };
    load();
  }, [activeTab, loadPending, loadMyRequests, loadRules]);

  const handleRespond = async (id, approved) => {
    try {
      setLoading(true);
      await approvalsAPI.respond(id, { approved, notes: respondNotes });
      setRespondModal(null);
      setRespondNotes('');
      showToast(approved ? 'Approved' : 'Rejected', 'success');
      loadPending();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (id) => {
    try {
      setLoading(true);
      await approvalsAPI.execute(id);
      showToast('Executed', 'success');
      loadMyRequests();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await approvalsAPI.cancel(id);
      showToast('Cancelled', 'success');
      loadMyRequests();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleSaveRule = async () => {
    try {
      setLoading(true);
      const data = { ...ruleForm, store_id: storeId };
      if (ruleForm.id) {
        await approvalsAPI.updateRule(ruleForm.id, data);
      } else {
        await approvalsAPI.createRule(data);
      }
      setRuleForm(null);
      showToast('Rule saved', 'success');
      loadRules();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await approvalsAPI.deleteRule(id);
      loadRules();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const urgentCount = pending.filter(p => p.priority === 'urgent').length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Approvals</h1>
      </div>

      {error && <div className="alert alert-danger">{error}<button className="inline-alert-close" onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button></div>}

      <div className="tab-bar">
        {TABS.map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'pending' ? 'Pending Approvals' : tab === 'my-requests' ? 'My Requests' : 'Rules'}
          </button>
        ))}
      </div>

      {/* Tab 1: Pending Approvals */}
      {activeTab === 'pending' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: '1rem' }}>
            <div className="kpi-card primary">
              <div className="kpi-label">Pending</div>
              <div className="kpi-value">{pending.length}</div>
            </div>
            <div className="kpi-card danger">
              <div className="kpi-label">Urgent</div>
              <div className="kpi-value">{urgentCount}</div>
            </div>
          </div>

          <div className="filter-bar approvals-filter-bar" style={{ marginBottom: '1rem' }}>
            <select className="form-input approvals-filter-select" value={pendingFilter.type} onChange={(e) => setPendingFilter({ ...pendingFilter, type: e.target.value })} style={{ maxWidth: '200px' }}>
              <option value="">All Types</option>
              <option value="stock_adjustment">Stock Adjustment</option>
              <option value="write_off">Write-Off</option>
              <option value="discount">Discount</option>
              <option value="refund">Refund</option>
              <option value="price_change">Price Change</option>
            </select>
            <select className="form-input approvals-filter-select" value={pendingFilter.priority} onChange={(e) => setPendingFilter({ ...pendingFilter, priority: e.target.value })} style={{ maxWidth: '160px' }}>
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Desktop Table */}
          <div className="card hidden-mobile">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Priority</th>
                    <th>Requester</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map(item => (
                    <tr key={item.id}>
                      <td className="font-bold">{item.title}</td>
                      <td>{item.approval_type?.replace(/_/g, ' ')}</td>
                      <td><span className={`badge ${PRIORITY_COLORS[item.priority] || 'badge-info'}`}>{item.priority}</span></td>
                      <td>{item.requester_name || item.requested_by}</td>
                      <td>{item.amount != null ? `$${Number(item.amount).toFixed(2)}` : '-'}</td>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <div className="approvals-action-row" style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-sm btn-success" onClick={() => { setRespondModal({ id: item.id, action: 'approve' }); setRespondNotes(''); }}>Approve</button>
                          <button className="btn btn-sm btn-danger" onClick={() => { setRespondModal({ id: item.id, action: 'reject' }); setRespondNotes(''); }}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pending.length === 0 && !loading && (
                    <tr><td colSpan={7} className="text-center text-muted" style={{ padding: '2rem' }}>No pending approvals</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="visible-mobile">
            {pending.map(item => (
              <div className="card" key={item.id} style={{ marginBottom: '0.75rem' }}>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span className="font-bold">{item.title}</span>
                    <span className={`badge ${PRIORITY_COLORS[item.priority] || 'badge-info'}`}>{item.priority}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    {item.approval_type?.replace(/_/g, ' ')} {item.amount != null ? `- $${Number(item.amount).toFixed(2)}` : ''}
                  </div>
                  <div className="approvals-action-row" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-sm btn-success" onClick={() => { setRespondModal({ id: item.id, action: 'approve' }); setRespondNotes(''); }}>Approve</button>
                    <button className="btn btn-sm btn-danger" onClick={() => { setRespondModal({ id: item.id, action: 'reject' }); setRespondNotes(''); }}>Reject</button>
                  </div>
                </div>
              </div>
            ))}
            {pending.length === 0 && !loading && <div className="empty-state"><p>No pending approvals</p></div>}
          </div>
        </>
      )}

      {/* Tab 2: My Requests */}
      {activeTab === 'my-requests' && (
        <>
          <div className="filter-bar approvals-filter-bar" style={{ marginBottom: '1rem' }}>
            <select className="form-input approvals-filter-select" value={myFilter.status} onChange={(e) => setMyFilter({ ...myFilter, status: e.target.value })} style={{ maxWidth: '200px' }}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Approver</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myRequests.map(item => (
                    <tr key={item.id}>
                      <td className="font-bold">{item.title}</td>
                      <td>{item.approval_type?.replace(/_/g, ' ')}</td>
                      <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-info'}`}>{item.status}</span></td>
                      <td>{item.amount != null ? `$${Number(item.amount).toFixed(2)}` : '-'}</td>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                      <td>{item.responded_by_name || item.responded_by || '-'}</td>
                      <td>
                        <div className="approvals-action-row" style={{ display: 'flex', gap: '0.5rem' }}>
                          {item.status === 'pending' && (
                            <button className="btn btn-sm btn-secondary" onClick={() => handleCancel(item.id)}>Cancel</button>
                          )}
                          {item.status === 'approved' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleExecute(item.id)}>Execute</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {myRequests.length === 0 && !loading && (
                    <tr><td colSpan={7} className="text-center text-muted" style={{ padding: '2rem' }}>No requests</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Tab 3: Rules */}
      {activeTab === 'rules' && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <button className="btn btn-primary" onClick={() => setRuleForm({
              approval_type: 'stock_adjustment', name: '', description: '',
              amount_threshold: '', percentage_threshold: '', min_role_required: 'manager', is_active: true,
            })}>
              Create Rule
            </button>
          </div>

          {ruleForm && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header"><h3 className="card-title">{ruleForm.id ? 'Edit Rule' : 'New Rule'}</h3></div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-input" value={ruleForm.approval_type} onChange={(e) => setRuleForm({ ...ruleForm, approval_type: e.target.value })}>
                      <option value="stock_adjustment">Stock Adjustment</option>
                      <option value="write_off">Write-Off</option>
                      <option value="discount">Discount</option>
                      <option value="refund">Refund</option>
                      <option value="price_change">Price Change</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-input" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount Threshold</label>
                    <input type="number" className="form-input" value={ruleForm.amount_threshold} onChange={(e) => setRuleForm({ ...ruleForm, amount_threshold: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Percentage Threshold</label>
                    <input type="number" className="form-input" value={ruleForm.percentage_threshold} onChange={(e) => setRuleForm({ ...ruleForm, percentage_threshold: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Min Role Required</label>
                    <select className="form-input" value={ruleForm.min_role_required} onChange={(e) => setRuleForm({ ...ruleForm, min_role_required: e.target.value })}>
                      <option value="manager">Manager</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={ruleForm.is_active} onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })} />
                      <span className="toggle-slider"></span>
                    </label>
                    <span style={{ fontSize: '0.85rem' }}>Active</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" rows={2} value={ruleForm.description} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })} />
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSaveRule} disabled={loading || !ruleForm.name}>Save</button>
                  <button className="btn btn-secondary" onClick={() => setRuleForm(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {rules.map(rule => (
              <div className="card" key={rule.id}>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span className="font-bold">{rule.name}</span>
                    <span className={`badge ${rule.is_active ? 'badge-success' : 'badge-info'}`}>{rule.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>{rule.description}</div>
                  <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    Type: {rule.approval_type?.replace(/_/g, ' ')}
                    {rule.amount_threshold != null && ` | Amount > $${rule.amount_threshold}`}
                    {rule.percentage_threshold != null && ` | % > ${rule.percentage_threshold}%`}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setRuleForm({ ...rule })}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteRule(rule.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
            {rules.length === 0 && !loading && <div className="empty-state"><p>No approval rules configured</p></div>}
          </div>
        </>
      )}

      {/* Respond Modal */}
      {respondModal && (
        <div className="modal-overlay" onClick={() => setRespondModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>{respondModal.action === 'approve' ? 'Approve Request' : 'Reject Request'}</h3>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows={3} value={respondNotes} onChange={(e) => setRespondNotes(e.target.value)} placeholder="Optional notes..." />
            </div>
            <div className="form-actions">
              <button
                className={`btn ${respondModal.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={() => handleRespond(respondModal.id, respondModal.action === 'approve')}
                disabled={loading}
              >
                {respondModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
              <button className="btn btn-secondary" onClick={() => setRespondModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="loading"><div className="spinner"></div><span>Loading...</span></div>}
    </div>
  );
};

export default Approvals;
